import { useState, useMemo } from 'react'
import {
  Plus, Tag, Send, Edit2, Trash2, Eye, RefreshCw, Users, Clock, Layers,
  CheckCircle, XCircle, Zap, Calendar, BarChart2, ChevronDown,
  Copy, X, Gift, Percent, DollarSign, Truck, Image as ImageIcon,
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Modal, Spinner, EmptyState } from '../components/ui'
import { promotionService, customerService, productService } from '../services/api'
import toast from 'react-hot-toast'
import { AdminPromoPreview, TEMPLATES } from '../components/PromoTemplates'

// ─── Constants ──────────────────────────────────────────────────────────────

const DISCOUNT_TYPES = [
  { value: 'percentage', label: 'Percentage Off', icon: Percent },
  { value: 'fixed',      label: 'Fixed Amount Off', icon: DollarSign },
  { value: 'free_shipping', label: 'Free Delivery', icon: Truck },
  { value: 'none',       label: 'No Discount (Info only)', icon: Gift },
]

const EMPTY_FORM = {
  title: '', description: '', promo_code: '',
  discount_type: 'percentage', discount_value: '',
  min_order_value: '', cta_label: 'Shop Now', cta_link: '/shop',
  banner_image: '', emoji: '', ribbon_text: '', layout: 'auto', image_scale: 100,
  ribbon_position: 'top-left', emoji_position: 'bottom-right', text_align: 'left',
  starts_at: '', ends_at: '', is_active: true, target_all: true, target_customer_ids: [],
}

// Curated emoji palette — liquor-forward, plus promo flair and a SA nod.
const EMOJI_SET = [
  '🍺','🍻','🍷','🥂','🍾','🥃','🍸','🍹','🧉','🍶',
  '🔥','💥','⭐','✨','🎉','🎊','💰','🏷️','🎁','🆕',
  '👑','🥇','☀️','❄️','🎄','🏉','🇿🇦','❤️','💚','⚡',
]

function EmojiPicker({ onPick, buttonClass = '', align = 'right' }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative flex-shrink-0">
      <button type="button" onClick={() => setOpen(o => !o)}
        className={buttonClass || 'px-3 py-2 rounded-lg bg-black/40 border border-[#323232] text-sm hover:text-white'}>
        😊
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
          <div className={`absolute ${align === 'left' ? 'left-0' : 'right-0'} z-50 mt-1 p-2 bg-[#1b1b1b] border border-[#323232] rounded-xl shadow-2xl grid grid-cols-10 gap-0.5 w-72`}>
            {EMOJI_SET.map(e => (
              <button key={e} type="button" onClick={() => { onPick(e); setOpen(false) }}
                className="text-lg hover:bg-white/10 rounded-md p-1 leading-none">{e}</button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// Corner position picker (shared by ribbon + emoji). Emoji adds a Center option.
const CORNERS = [
  { v: 'top-left', icon: '◤', label: 'Top left' },
  { v: 'top-right', icon: '◥', label: 'Top right' },
  { v: 'bottom-left', icon: '◣', label: 'Bottom left' },
  { v: 'bottom-right', icon: '◢', label: 'Bottom right' },
]
const EMOJI_CORNERS = [...CORNERS, { v: 'center', icon: '●', label: 'Center' }]

function PositionPicker({ value, onChange, options = CORNERS }) {
  return (
    <div className="flex gap-1 mt-1.5">
      {options.map(o => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)} title={o.label}
          className={`w-7 h-7 rounded-lg border text-xs leading-none transition-colors ${
            value === o.v
              ? 'bg-primary/15 border-primary/50 text-primary'
              : 'bg-black/40 border-[#323232] text-gray-500 hover:text-gray-200'
          }`}>
          {o.icon}
        </button>
      ))}
    </div>
  )
}

// Resolve a stored image path to a servable URL.
function imgUrl(u) {
  if (!u) return ''
  return u.startsWith('http') || u.startsWith('/') ? u : `/MappedData/${u}`
}

// Pick a promo banner image from a product's available images.
function ProductImagePicker({ value, onPick }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [product, setProduct] = useState(null)

  const { data: searchData, isFetching } = useQuery({
    queryKey: ['promo-img-search', q],
    queryFn: () => productService.getAll({ search: q.trim(), limit: 12 }),
    enabled: open && q.trim().length >= 2,
    staleTime: 60000,
  })
  const products = searchData?.items ?? (Array.isArray(searchData) ? searchData : [])

  const { data: imgData, isFetching: imgLoading } = useQuery({
    queryKey: ['promo-img-list', product?.id],
    queryFn: () => productService.getImages(product.id),
    enabled: !!product,
    staleTime: 60000,
  })
  const images = imgData?.images ?? []

  const fieldCls = "w-full bg-black/40 border border-[#323232] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-primary/60 placeholder:text-gray-600"

  return (
    <div>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={imgUrl(value)} alt="" className="w-14 h-14 rounded-lg object-cover border border-[#323232]"/>
        ) : (
          <div className="w-14 h-14 rounded-lg bg-black/40 border border-[#323232] flex items-center justify-center text-gray-600">
            <ImageIcon size={18}/>
          </div>
        )}
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setOpen(o => !o)}
            className="text-xs px-3 py-1.5 rounded-lg bg-black/40 border border-[#323232] text-gray-300 hover:text-white hover:border-primary/40 transition-colors">
            {open ? 'Close picker' : 'Pick from a product'}
          </button>
          {value && (
            <button type="button" onClick={() => onPick('')}
              className="text-xs text-gray-500 hover:text-red-400">Clear</button>
          )}
        </div>
      </div>

      {open && (
        <div className="mt-2 p-3 rounded-xl bg-black/30 border border-[#323232] space-y-3">
          <input autoFocus value={q} onChange={e => { setQ(e.target.value); setProduct(null) }}
            placeholder="Search a product by name or stock code…" className={fieldCls}/>

          {!product ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-y-auto">
              {isFetching && <p className="text-xs text-gray-500 col-span-full">Searching…</p>}
              {!isFetching && q.trim().length >= 2 && products.length === 0 &&
                <p className="text-xs text-gray-500 col-span-full">No products found.</p>}
              {!isFetching && q.trim().length < 2 &&
                <p className="text-xs text-gray-600 col-span-full">Type at least 2 characters to search.</p>}
              {products.map(p => (
                <button key={p.id} type="button" onClick={() => setProduct(p)}
                  className="flex items-center gap-2 p-2 rounded-lg bg-black/40 border border-[#323232] hover:border-primary/50 text-left">
                  {p.primary_image
                    ? <img src={imgUrl(p.primary_image)} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0"/>
                    : <div className="w-8 h-8 rounded bg-black/40 flex-shrink-0"/>}
                  <span className="text-xs text-gray-300 truncate">{p.name || p.description}</span>
                </button>
              ))}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-400 truncate">{product.name || product.description}</span>
                <button type="button" onClick={() => setProduct(null)}
                  className="text-xs text-primary hover:underline flex-shrink-0 ml-2">← products</button>
              </div>
              {imgLoading ? (
                <p className="text-xs text-gray-500">Loading images…</p>
              ) : images.length === 0 ? (
                <p className="text-xs text-gray-500">No images available for this product.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto">
                  {images.map((im, i) => (
                    <button key={im.url || i} type="button"
                      onClick={() => { onPick(im.url, product); setOpen(false) }}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-colors ${
                        value === im.url ? 'border-primary' : 'border-transparent hover:border-primary/40'
                      }`}>
                      <img src={im.url} alt="" className="w-full h-full object-cover"/>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-ZA', { day:'numeric', month:'short', year:'numeric' }) : '—'
const fmtR = (n) => `R ${parseFloat(n||0).toFixed(2)}`

function statusOf(p) {
  const now = new Date()
  if (!p.is_active) return { label: 'Inactive', color: 'text-gray-400', bg: 'bg-gray-400/10', border: 'border-gray-400/20' }
  if (p.starts_at && new Date(p.starts_at) > now) return { label: 'Scheduled', color: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/20' }
  if (p.ends_at && new Date(p.ends_at) < now) return { label: 'Expired', color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/20' }
  return { label: 'Live', color: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/20' }
}

function discountLabel(p) {
  if (p.discount_type === 'percentage') return `${p.discount_value || 0}% off`
  if (p.discount_type === 'fixed') return `R${p.discount_value || 0} off`
  if (p.discount_type === 'free_shipping') return 'Free delivery'
  return 'Info only'
}

// ─── Custom DateTime Picker ──────────────────────────────────────────────────
// Renders separate day / month / year / hour / minute selects.
// Value in/out: ISO string ("2026-03-15T14:30:00") or '' for blank.

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

function DateTimePicker({ label, hint, value, onChange }) {
  // Parse ISO → parts (using local time, not UTC)
  const parse = (iso) => {
    if (!iso) return { d:'', mo:'', y:'', h:'', mi:'' }
    const dt = new Date(iso)
    if (isNaN(dt)) return { d:'', mo:'', y:'', h:'', mi:'' }
    return {
      d:  String(dt.getDate()),
      mo: String(dt.getMonth() + 1),
      y:  String(dt.getFullYear()),
      h:  String(dt.getHours()).padStart(2,'0'),
      mi: String(dt.getMinutes()).padStart(2,'0'),
    }
  }
  const [parts, setParts] = useState(() => parse(value))

  // Rebuild ISO whenever parts change
  const update = (newParts) => {
    setParts(newParts)
    const { d, mo, y, h, mi } = newParts
    if (d && mo && y) {
      const month = String(mo).padStart(2,'0')
      const day   = String(d).padStart(2,'0')
      const hour  = h  || '00'
      const min   = mi || '00'
      const iso = `${y}-${month}-${day}T${hour}:${min}:00`
      onChange(iso)
    } else {
      onChange('')
    }
  }

  const sel = "bg-black/40 border border-[#323232] rounded-lg text-white text-sm px-2 py-2 focus:outline-none focus:border-primary/60 appearance-none"
  const days   = Array.from({length:31}, (_,i) => i+1)
  const years  = Array.from({length:8},  (_,i) => new Date().getFullYear() + i)
  const hours  = Array.from({length:24}, (_,i) => String(i).padStart(2,'0'))
  const mins   = ['00','05','10','15','20','25','30','35','40','45','50','55']

  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1.5 font-medium">
        {label} {hint && <span className="text-gray-600 font-normal">{hint}</span>}
      </label>
      <div className="flex flex-wrap gap-1.5 items-center">
        {/* Day */}
        <select className={sel + ' w-16'} value={parts.d} onChange={e=>update({...parts,d:e.target.value})}>
          <option value="">DD</option>
          {days.map(d=><option key={d} value={d}>{String(d).padStart(2,'0')}</option>)}
        </select>
        {/* Month */}
        <select className={sel + ' w-16'} value={parts.mo} onChange={e=>update({...parts,mo:e.target.value})}>
          <option value="">Mon</option>
          {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
        </select>
        {/* Year */}
        <select className={sel + ' w-24'} value={parts.y} onChange={e=>update({...parts,y:e.target.value})}>
          <option value="">Year</option>
          {years.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-gray-600 text-sm px-0.5">at</span>
        {/* Hour */}
        <select className={sel + ' w-16'} value={parts.h} onChange={e=>update({...parts,h:e.target.value})}>
          {hours.map(h=><option key={h} value={h}>{h}h</option>)}
        </select>
        <span className="text-gray-600 text-sm">:</span>
        {/* Minute */}
        <select className={sel + ' w-16'} value={parts.mi} onChange={e=>update({...parts,mi:e.target.value})}>
          {mins.map(m=><option key={m} value={m}>{m}</option>)}
        </select>
        {/* Clear */}
        {(parts.d || parts.mo || parts.y) && (
          <button
            onClick={() => { setParts({d:'',mo:'',y:'',h:'00',mi:'00'}); onChange('') }}
            className="text-xs text-gray-600 hover:text-red-400 ml-1 transition-colors"
          >
            ✕ Clear
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Promotion Form Modal ────────────────────────────────────────────────────

function PromotionFormModal({ initial, onClose, onSave }) {
  const [form, setForm]     = useState(() => ({ ...EMPTY_FORM, ...(initial || {}) }))
  const [saving, setSaving] = useState(false)
  const [custSearch, setCustSearch] = useState('')
  const [targetMode, setTargetMode] = useState(form.target_all ? 'all' : 'specific')
  const [targetTiers, setTargetTiers] = useState(new Set())

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const needsValue = ['percentage', 'fixed'].includes(form.discount_type)

  const { data: customersData, isLoading: custsLoading } = useQuery({
    queryKey: ['admin', 'customers-for-form'],
    queryFn: () => customerService.getAll({ limit: 500 }),
    staleTime: 5 * 60 * 1000,
    enabled: !form.target_all,
  })
  const allCustomers = Array.isArray(customersData) ? customersData : (customersData?.items ?? customersData?.customers ?? [])
  const filteredCustomers = allCustomers.filter(c =>
    c.name?.toLowerCase().includes(custSearch.toLowerCase()) ||
    c.email?.toLowerCase().includes(custSearch.toLowerCase())
  )
  const selectedIds = new Set(form.target_customer_ids || [])
  const toggleCustomer = (id) => {
    const next = new Set(selectedIds)
    next.has(id) ? next.delete(id) : next.add(id)
    set('target_customer_ids', [...next])
  }
  const availableTiers = useMemo(
    () => [...new Set(allCustomers.map(c => c.price_list_tier || 2))].sort((a, b) => a - b),
    [allCustomers],
  )
  const toggleTier = (t) => {
    const next = new Set(targetTiers)
    next.has(t) ? next.delete(t) : next.add(t)
    setTargetTiers(next)
    set('target_customer_ids', allCustomers.filter(c => next.has(c.price_list_tier || 2)).map(c => c.id))
  }

  const handleSubmit = async () => {
    if (!form.title.trim()) return toast.error('Title is required')
    if (needsValue && !form.discount_value) return toast.error('Discount value is required')
    setSaving(true)
    try {
      // Marshal numeric fields — never send empty strings
      const payload = {
        ...form,
        discount_value:  form.discount_value  !== '' ? parseFloat(form.discount_value)  : null,
        min_order_value: form.min_order_value !== '' ? parseFloat(form.min_order_value) : null,
        starts_at: form.starts_at || null,
        ends_at:   form.ends_at   || null,
      }
      await onSave(payload)
      onClose()
    } finally {
      setSaving(false)
    }
  }

  const inputCls = "w-full bg-black/40 border border-[#323232] rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-primary/60 placeholder:text-gray-600"
  const labelCls = "block text-xs text-gray-400 mb-1.5 font-medium"

  return (
    <Modal title={initial ? 'Edit Promotion' : 'Create Promotion'} onClose={onClose} size="xl">
      <div className="flex gap-5 max-h-[80vh]">

        {/* ── Left: form fields ─────────────────────────────────────── */}
        <div className="flex-1 min-w-0 space-y-4 overflow-y-auto pr-1">

          {/* Template picker */}
          <div>
            <label className={labelCls}>Visual Template</label>
            <div className="grid grid-cols-5 gap-2">
              {TEMPLATES.map(t => {
                const Icon = t.icon
                const sel = form.template === t.id
                return (
                  <button
                    key={t.id}
                    title={t.desc}
                    onClick={() => set('template', t.id)}
                    className={`relative flex flex-col items-center gap-1.5 p-2.5 rounded-xl border text-center transition-all ${
                      sel ? 'border-primary/60 ring-1 ring-primary/40 scale-[1.03]' : 'border-[#323232] hover:border-gray-500'
                    }`}
                    style={{ background: sel ? 'rgba(14,148,122,0.08)' : 'rgba(0,0,0,0.4)' }}
                  >
                    {/* Colour swatch */}
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                         style={{ background: t.preview_gradient }}>
                      <Icon size={14} className="text-white"/>
                    </div>
                    <span className={`text-xs font-medium leading-tight ${sel ? 'text-primary' : 'text-gray-400'}`}>
                      {t.label}
                    </span>
                  </button>
                )
              })}
            </div>
            <p className="text-gray-600 text-xs mt-1.5">
              {TEMPLATES.find(t => t.id === form.template)?.desc}
            </p>
          </div>

          {/* Accent colour override */}
          <div>
            <label className={labelCls}>Accent Colour <span className="text-gray-600">(optional — overrides template colour)</span></label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.accent_color || '#0e947a'}
                onChange={e => set('accent_color', e.target.value)}
                className="w-10 h-10 rounded-lg border border-[#323232] cursor-pointer bg-transparent"
              />
              <input
                className={inputCls + ' flex-1'}
                placeholder="#0e947a (leave blank for template default)"
                value={form.accent_color}
                onChange={e => set('accent_color', e.target.value)}
              />
              {form.accent_color && (
                <button onClick={() => set('accent_color', '')}
                  className="text-gray-500 hover:text-gray-300 text-xs px-2 py-1 border border-[#323232] rounded-lg">
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Flair: hero emoji + corner ribbon */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Hero Emoji <span className="text-gray-600">(optional)</span></label>
              <div className="flex items-center gap-2">
                <div className="w-11 h-11 rounded-xl bg-black/40 border border-[#323232] flex items-center justify-center text-2xl leading-none">
                  {form.emoji || <span className="text-gray-600 text-sm">—</span>}
                </div>
                <EmojiPicker onPick={e => set('emoji', e)} align="left"/>
                {form.emoji && (
                  <button onClick={() => set('emoji', '')}
                    className="text-gray-500 hover:text-red-400 text-xs">Clear</button>
                )}
              </div>
              {form.emoji && (
                <PositionPicker value={form.emoji_position || 'bottom-right'}
                  onChange={v => set('emoji_position', v)} options={EMOJI_CORNERS}/>
              )}
            </div>
            <div>
              <label className={labelCls}>Corner Ribbon <span className="text-gray-600">(optional)</span></label>
              <input className={inputCls} maxLength={18} placeholder="e.g. SALE"
                value={form.ribbon_text} onChange={e => set('ribbon_text', e.target.value)}/>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {['SALE','NEW','HOT','LIMITED','2-FOR-1'].map(r => (
                  <button key={r} type="button" onClick={() => set('ribbon_text', r)}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-black/40 border border-[#323232] text-gray-400 hover:text-white hover:border-primary/40 transition-colors">
                    {r}
                  </button>
                ))}
              </div>
              {form.ribbon_text && (
                <PositionPicker value={form.ribbon_position || 'top-left'}
                  onChange={v => set('ribbon_position', v)}/>
              )}
            </div>
          </div>

          {/* Banner image + layout */}
          <div>
            <label className={labelCls}>Product Image <span className="text-gray-600">(optional — sets the card image and points the button at this product)</span></label>
            <ProductImagePicker value={form.banner_image} onPick={(u, prod) => {
              set('banner_image', u)
              if (prod?.stock_code) { set('cta_link', `/shop?search=${prod.stock_code}`) }
            }}/>
            <input className={inputCls + ' mt-2'} placeholder="…or paste an image URL directly"
              value={form.banner_image} onChange={e => set('banner_image', e.target.value)}/>
            <div className="grid grid-cols-4 gap-2 mt-2">
              {[
                { v: 'auto',       label: 'Auto',    hint: 'Image if set' },
                { v: 'solid',      label: 'Solid',   hint: 'Colour only' },
                { v: 'image_bg',   label: 'Photo BG', hint: 'Full image' },
                { v: 'image_side', label: 'Split',   hint: 'Image + text' },
              ].map(l => {
                const sel = (form.layout || 'auto') === l.v
                const dim = (l.v === 'image_bg' || l.v === 'image_side') && !form.banner_image
                return (
                  <button key={l.v} type="button" onClick={() => set('layout', l.v)}
                    className={`px-2 py-2 rounded-xl border text-center transition-colors ${sel ? 'bg-primary/10 border-primary/50 text-primary' : 'bg-black/40 border-[#323232] text-gray-400 hover:text-gray-200'}`}>
                    <div className="text-xs font-semibold">{l.label}</div>
                    <div className={`text-[9px] mt-0.5 ${dim ? 'text-gray-600' : 'text-gray-500'}`}>{dim ? 'needs image' : l.hint}</div>
                  </button>
                )
              })}
            </div>
            {form.banner_image && (
              <div className="mt-3">
                <label className={labelCls}>Image size <span className="text-gray-600">({form.image_scale || 100}%)</span></label>
                <input type="range" min="40" max="160" step="5"
                  value={form.image_scale || 100}
                  onChange={e => set('image_scale', parseInt(e.target.value) || 100)}
                  className="w-full accent-primary cursor-pointer"/>
                <div className="flex justify-between text-[10px] text-gray-600"><span>Smaller</span><span>Larger</span></div>
              </div>
            )}
            <div className="mt-3">
              <label className={labelCls}>Text position</label>
              <div className="flex gap-2">
                {[{ v: 'left', label: 'Left' }, { v: 'center', label: 'Center' }].map(t => (
                  <button key={t.v} type="button" onClick={() => set('text_align', t.v)}
                    className={`flex-1 px-3 py-2 rounded-xl border text-xs font-semibold transition-colors ${
                      (form.text_align || 'left') === t.v
                        ? 'bg-primary/10 border-primary/50 text-primary'
                        : 'bg-black/40 border-[#323232] text-gray-400 hover:text-gray-200'
                    }`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Title + code */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1">
              <label className={labelCls}>Title *</label>
              <div className="flex gap-2">
                <input className={inputCls + ' flex-1'} placeholder="e.g. 10% Off All Wine" value={form.title} onChange={e=>set('title',e.target.value)}/>
                <EmojiPicker onPick={e => set('title', (form.title || '') + e)}
                  buttonClass="px-3 rounded-lg bg-black/40 border border-[#323232] text-sm hover:text-white flex-shrink-0"/>
              </div>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className={labelCls}>Promo Code <span className="text-gray-600">(optional)</span></label>
              <input className={inputCls} placeholder="e.g. WINE10" value={form.promo_code} onChange={e=>set('promo_code',e.target.value.toUpperCase())}/>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className={labelCls}>Description / Details</label>
            <textarea className={inputCls + ' resize-none'} rows={3} placeholder="Full details shown to the customer…" value={form.description} onChange={e=>set('description',e.target.value)}/>
          </div>

          {/* Discount type */}
          <div>
            <label className={labelCls}>Discount Type</label>
            <div className="grid grid-cols-2 gap-2">
              {DISCOUNT_TYPES.map(dt => {
                const Icon = dt.icon
                const sel = form.discount_type === dt.value
                return (
                  <button key={dt.value} onClick={() => set('discount_type', dt.value)}
                    className={`flex items-center gap-2 p-2.5 rounded-xl border text-sm transition-colors ${
                      sel ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-black/40 border-[#323232] text-gray-400 hover:text-gray-300'
                    }`}>
                    <Icon size={14}/> {dt.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Discount value + min order */}
          {needsValue && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>{form.discount_type === 'percentage' ? 'Discount %' : 'Discount Amount (R)'}</label>
                <input type="number" min="0" className={inputCls} placeholder={form.discount_type === 'percentage' ? '10' : '50'}
                  value={form.discount_value} onChange={e=>set('discount_value', e.target.value)}/>
              </div>
              <div>
                <label className={labelCls}>Min Order Value (R) <span className="text-gray-600">(optional)</span></label>
                <input type="number" min="0" className={inputCls} placeholder="0"
                  value={form.min_order_value} onChange={e=>set('min_order_value', e.target.value)}/>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Button Label</label>
              <input className={inputCls} placeholder="Shop Now" value={form.cta_label} onChange={e=>set('cta_label',e.target.value)}/>
            </div>
            <div>
              <label className={labelCls}>Button Link</label>
              <input className={inputCls} placeholder="/shop" value={form.cta_link} onChange={e=>set('cta_link',e.target.value)}/>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 gap-3">
            <DateTimePicker label="Starts At" hint="(leave blank = immediately active)" value={form.starts_at} onChange={v => set('starts_at', v)}/>
            <DateTimePicker label="Ends At"   hint="(leave blank = no expiry)"          value={form.ends_at}   onChange={v => set('ends_at',   v)}/>
          </div>

          {/* Targeting */}
          <div className="bg-black/30 border border-[#323232] rounded-xl p-3 space-y-3">
            <label className={labelCls + ' mb-0'}>Targeting</label>
            <div className="flex gap-2">
              {[
                { m: 'all',      icon: Users,  label: 'All Active' },
                { m: 'tier',     icon: Layers, label: 'By Tier' },
                { m: 'specific', icon: Tag,    label: 'Specific' },
              ].map(({ m, icon: Icon, label }) => (
                <button key={m} type="button"
                  onClick={() => {
                    setTargetMode(m)
                    set('target_all', m === 'all')
                    set('target_customer_ids', [])
                    setTargetTiers(new Set())
                    setCustSearch('')
                  }}
                  className={`flex-1 py-2 rounded-xl border text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${targetMode === m ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-black/40 border-[#323232] text-gray-400 hover:text-gray-300'}`}>
                  <Icon size={13}/> {label}
                </button>
              ))}
            </div>

            {targetMode === 'tier' && (
              <div className="space-y-2">
                {custsLoading ? <div className="flex justify-center py-3"><Spinner size={16}/></div> : (
                  <div className="flex flex-wrap gap-2">
                    {availableTiers.length === 0 && <p className="text-gray-500 text-xs">No customers found</p>}
                    {availableTiers.map(t => {
                      const count = allCustomers.filter(c => (c.price_list_tier || 2) === t).length
                      return (
                        <button key={t} type="button" onClick={() => toggleTier(t)}
                          className={`px-3 py-1.5 rounded-xl border text-sm font-medium transition-colors ${targetTiers.has(t) ? 'bg-primary/10 border-primary/50 text-primary' : 'bg-black/40 border-[#323232] text-gray-400 hover:text-gray-200'}`}>
                          Tier {t} <span className="text-gray-500">({count})</span>
                        </button>
                      )
                    })}
                  </div>
                )}
                {form.target_customer_ids?.length > 0 && (
                  <p className="text-xs text-primary">
                    {form.target_customer_ids.length} customer{form.target_customer_ids.length !== 1 ? 's' : ''} across {targetTiers.size} tier{targetTiers.size !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            )}

            {targetMode === 'specific' && (
              <div className="space-y-2">
                <input value={custSearch} onChange={e => setCustSearch(e.target.value)}
                  placeholder="Search customers by name or email…"
                  className="w-full bg-black/40 border border-[#323232] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-primary/60 placeholder:text-gray-600"/>
                {custsLoading ? <div className="flex justify-center py-3"><Spinner size={16}/></div> : (
                  <div className="max-h-40 overflow-y-auto border border-surface-border rounded-xl p-1 space-y-0.5">
                    {filteredCustomers.length === 0 && <p className="text-gray-500 text-xs p-2 text-center">No customers found</p>}
                    {filteredCustomers.map(c => (
                      <button key={c.id} type="button" onClick={() => toggleCustomer(c.id)}
                        className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors text-sm ${selectedIds.has(c.id) ? 'bg-primary/10 text-primary' : 'text-gray-300 hover:bg-white/5'}`}>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${selectedIds.has(c.id) ? 'bg-primary border-primary' : 'border-gray-600'}`}>
                          {selectedIds.has(c.id) && <CheckCircle size={10} className="text-white"/>}
                        </div>
                        <span className="font-medium truncate">{c.name}</span>
                        {c.email && <span className="text-gray-500 text-xs truncate">{c.email}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {selectedIds.size > 0 && <p className="text-xs text-primary">{selectedIds.size} customer{selectedIds.size !== 1 ? 's' : ''} selected</p>}
              </div>
            )}
          </div>

          {/* Active toggle */}
          <div className="bg-black/30 border border-[#323232] rounded-xl p-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <div onClick={() => set('is_active', !form.is_active)}
                className={`w-10 h-5 rounded-full transition-colors relative ${form.is_active ? 'bg-primary' : 'bg-gray-600'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.is_active ? 'translate-x-5' : 'translate-x-0.5'}`}/>
              </div>
              <span className="text-sm text-gray-300">Active (visible to customers)</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-3 mt-1 border-t border-surface-border">
            <button onClick={onClose} className="btn-secondary">Cancel</button>
            <button onClick={handleSubmit} disabled={saving} className="btn-primary flex items-center gap-1.5">
              {saving ? <Spinner size={14}/> : <CheckCircle size={14}/>}
              {initial ? 'Save Changes' : 'Create Promotion'}
            </button>
          </div>
        </div>

        {/* ── Right: live preview ───────────────────────────────────── */}
        <div className="w-72 flex-shrink-0 hidden lg:flex flex-col gap-3 overflow-y-auto">
          <div className="sticky top-0 bg-surface-card z-10 pb-2 border-b border-surface-border">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Live Preview</p>
            <p className="text-gray-600 text-xs mt-0.5">Updates as you type</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1.5">📣 Banner Strip</p>
            <AdminPromoPreview promo={form} mode="banner"/>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1.5">💬 Popup Card</p>
            <AdminPromoPreview promo={form} mode="popup"/>
          </div>
          <div>
            <p className="text-xs text-gray-500 mb-1.5">🗂 Promotions Page</p>
            <AdminPromoPreview promo={form} mode="card"/>
          </div>
        </div>

      </div>
    </Modal>
  )

}


// ─── Push Modal ───────────────────────────────────────────────────────────────

function PushModal({ promo, onClose }) {
  const [mode, setMode] = useState(promo.target_all ? 'all' : 'specific')
  const [selectedIds, setSelectedIds] = useState(() => new Set(promo.target_customer_ids || []))
  const [tiers, setTiers] = useState(new Set())
  const [search, setSearch] = useState('')
  const [pushing, setPushing] = useState(false)
  const qc = useQueryClient()

  const { data: customersData, isLoading } = useQuery({
    queryKey: ['admin', 'customers-for-push'],
    queryFn: () => customerService.getAll({ limit: 500 }),
    staleTime: 5 * 60 * 1000,
    enabled: mode !== 'all',
  })

  const customers = Array.isArray(customersData) ? customersData : (customersData?.items ?? customersData?.customers ?? [])
  const filtered = customers.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase()))
  const availableTiers = [...new Set(customers.map(c => c.price_list_tier || 2))].sort((a, b) => a - b)
  const tierIds = customers.filter(c => tiers.has(c.price_list_tier || 2)).map(c => c.id)
  const toggleTier = (t) => setTiers(prev => { const n = new Set(prev); n.has(t) ? n.delete(t) : n.add(t); return n })

  const toggleCustomer = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handlePush = async () => {
    setPushing(true)
    try {
      const ids = mode === 'all' ? null : (mode === 'tier' ? tierIds : [...selectedIds])
      if (mode !== 'all' && ids.length === 0) {
        toast.error(mode === 'tier' ? 'Select at least one tier' : 'Select at least one customer'); return
      }
      const res = await promotionService.push(promo.id, ids)
      toast.success(`Pushed to ${res.pushed} customer${res.pushed !== 1 ? 's' : ''}!`)
      qc.invalidateQueries(['admin', 'promotions'])
      onClose()
    } catch {
      toast.error('Push failed')
    } finally {
      setPushing(false)
    }
  }

  return (
    <Modal title={`Push: ${promo.title}`} onClose={onClose} size="lg">
      <div className="space-y-4">
        <div className="bg-black/30 border border-[#323232] rounded-xl p-4">
          <p className="text-sm text-gray-400 mb-3">Choose who receives this promotion notification:</p>
          <div className="flex gap-3">
            {[
              { m: 'all',      icon: Users,  label: 'All Active' },
              { m: 'tier',     icon: Layers, label: 'By Tier' },
              { m: 'specific', icon: Tag,    label: 'Specific' },
            ].map(({ m, icon: Icon, label }) => (
              <button key={m}
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-colors ${mode === m ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-surface-card border-surface-border text-gray-400 hover:text-gray-300'}`}
              >
                <Icon size={14} className="inline mr-1.5"/>{label}
              </button>
            ))}
          </div>
        </div>

        {mode === 'tier' && (
          <div className="bg-black/30 border border-[#323232] rounded-xl p-4">
            {isLoading ? <div className="flex justify-center py-3"><Spinner/></div> : (
              <div className="flex flex-wrap gap-2">
                {availableTiers.length === 0 && <p className="text-gray-500 text-sm">No customers found</p>}
                {availableTiers.map(t => {
                  const count = customers.filter(c => (c.price_list_tier || 2) === t).length
                  return (
                    <button key={t} onClick={() => toggleTier(t)}
                      className={`px-3.5 py-2 rounded-xl border text-sm font-medium transition-colors ${tiers.has(t) ? 'bg-primary/10 border-primary/50 text-primary' : 'bg-surface-card border-surface-border text-gray-400 hover:text-gray-200'}`}>
                      Tier {t} <span className="text-gray-500">({count})</span>
                    </button>
                  )
                })}
              </div>
            )}
            {tierIds.length > 0 && (
              <p className="text-xs text-primary mt-3">{tierIds.length} customer{tierIds.length !== 1 ? 's' : ''} across {tiers.size} tier{tiers.size !== 1 ? 's' : ''}</p>
            )}
          </div>
        )}

        {mode === 'specific' && (
          <div>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search customers…"
              className="w-full bg-black/40 border border-[#323232] rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-primary/60 mb-2"
            />
            {isLoading ? (
              <div className="flex justify-center py-4"><Spinner/></div>
            ) : (
              <div className="max-h-48 overflow-y-auto space-y-1 border border-surface-border rounded-xl p-1">
                {filtered.length === 0 && <p className="text-gray-500 text-sm p-2 text-center">No customers found</p>}
                {filtered.map(c => (
                  <button
                    key={c.id}
                    onClick={() => toggleCustomer(c.id)}
                    className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors text-sm ${selectedIds.has(c.id) ? 'bg-primary/10 text-primary' : 'text-gray-300 hover:bg-white/5'}`}
                  >
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${selectedIds.has(c.id) ? 'bg-primary border-primary' : 'border-gray-600'}`}>
                      {selectedIds.has(c.id) && <CheckCircle size={10} className="text-white"/>}
                    </div>
                    <span className="font-medium">{c.name}</span>
                    {c.email && <span className="text-gray-500 text-xs">{c.email}</span>}
                  </button>
                ))}
              </div>
            )}
            {selectedIds.size > 0 && (
              <p className="text-xs text-primary mt-2">{selectedIds.size} customer{selectedIds.size !== 1 ? 's' : ''} selected</p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1 border-t border-surface-border">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={handlePush} disabled={pushing} className="btn-primary flex items-center gap-1.5">
            {pushing ? <Spinner size={14}/> : <Send size={14}/>}
            {mode === 'all' ? 'Push to All Customers'
              : mode === 'tier' ? `Push to ${tierIds.length} in ${tiers.size} Tier${tiers.size !== 1 ? 's' : ''}`
              : `Push to ${selectedIds.size} Selected`}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ─── Promo Card ───────────────────────────────────────────────────────────────

function PromoCard({ promo, onEdit, onDelete, onPush, onView }) {
  const st = statusOf(promo)
  const [showMenu, setShowMenu] = useState(false)

  return (
    <div className="bg-surface-card border border-surface-border rounded-2xl overflow-hidden hover:border-gray-500 transition-colors group">
      {/* Header stripe */}
      <div className={`h-1.5 w-full ${st.label === 'Live' ? 'bg-primary' : st.label === 'Scheduled' ? 'bg-blue-500' : st.label === 'Expired' ? 'bg-red-600' : 'bg-gray-600'}`}/>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.color} border ${st.border}`}>
                {st.label === 'Live' && <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1 animate-pulse"/>}
                {st.label}
              </span>
              {promo.promo_code && (
                <span className="font-mono text-xs bg-black/40 border border-[#323232] text-gold px-2 py-0.5 rounded-lg">
                  {promo.promo_code}
                </span>
              )}
            </div>
            <h3 className="text-white font-semibold text-sm leading-tight">{promo.title}</h3>
            {promo.description && (
              <p className="text-gray-500 text-xs mt-1 line-clamp-2">{promo.description}</p>
            )}
          </div>
          <div className="relative flex-shrink-0">
            <button onClick={() => setShowMenu(!showMenu)} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors">
              <ChevronDown size={14}/>
            </button>
            {showMenu && (
              <div className="absolute right-0 top-full mt-1 bg-surface-card border border-surface-border rounded-xl shadow-xl z-20 w-36 overflow-hidden">
                <button onClick={()=>{onEdit();setShowMenu(false)}} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5">
                  <Edit2 size={12}/> Edit
                </button>
                <button onClick={()=>{onView();setShowMenu(false)}} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-white/5">
                  <Eye size={12}/> View Targets
                </button>
                <button onClick={()=>{onDelete();setShowMenu(false)}} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/5">
                  <Trash2 size={12}/> Delete
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Discount badge */}
        <div className="flex items-center gap-2 mb-3">
          <span className="text-primary text-xs font-semibold bg-primary/10 border border-primary/20 px-2 py-1 rounded-lg flex items-center gap-1">
            <Tag size={11}/> {discountLabel(promo)}
          </span>
          {promo.min_order_value > 0 && (
            <span className="text-gray-400 text-xs">min {fmtR(promo.min_order_value)}</span>
          )}
        </div>

        {/* Dates */}
        <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
          <span className="flex items-center gap-1"><Calendar size={11}/> {promo.starts_at ? fmt(promo.starts_at) : 'Now'}</span>
          {promo.ends_at && <span>→ {fmt(promo.ends_at)}</span>}
          {!promo.ends_at && <span className="text-gray-600">No expiry</span>}
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3 text-xs text-gray-500 border-t border-surface-border pt-3 mb-3">
          <span className="flex items-center gap-1">
            <Send size={11} className="text-blue-400"/> {promo.sent_count} sent
          </span>
          <span className="flex items-center gap-1">
            <Eye size={11} className="text-green-400"/> {promo.read_count} read
          </span>
          <span className="flex items-center gap-1">
            {promo.target_all ? <><Users size={11} className="text-amber-400"/> All</> : <><Tag size={11} className="text-amber-400"/> {promo.target_count} targeted</>}
          </span>
        </div>

        {/* Push button */}
        <button
          onClick={onPush}
          className="w-full btn-primary text-xs py-2 flex items-center justify-center gap-1.5"
        >
          <Send size={12}/> Push Notification
        </button>
      </div>
    </div>
  )
}

// ─── Targets Viewer ────────────────────────────────────────────────────────────

function TargetsModal({ promo, onClose }) {
  const { data: targets, isLoading } = useQuery({
    queryKey: ['admin', 'promo-targets', promo.id],
    queryFn: () => promotionService.getTargets(promo.id),
    staleTime: 30 * 1000,
  })

  const list = Array.isArray(targets) ? targets : []
  const readCount = list.filter(t => t.read_at).length

  return (
    <Modal title={`Targets — ${promo.title}`} onClose={onClose} size="lg">
      <div className="space-y-3">
        <div className="flex items-center gap-4 text-sm text-gray-400">
          <span className="flex items-center gap-1"><Send size={13} className="text-blue-400"/> {list.length} sent</span>
          <span className="flex items-center gap-1"><Eye size={13} className="text-green-400"/> {readCount} read</span>
          {list.length > 0 && (
            <span className="text-gray-500">{Math.round((readCount/list.length)*100)}% open rate</span>
          )}
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner size="lg"/></div>
        ) : list.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">Not pushed to anyone yet</div>
        ) : (
          <div className="max-h-80 overflow-y-auto border border-surface-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="admin-thead border-b border-[#323232]">
                  <th className="text-left px-3 py-2 text-gray-400 text-xs font-medium">Customer</th>
                  <th className="text-left px-3 py-2 text-gray-400 text-xs font-medium">Sent</th>
                  <th className="text-left px-3 py-2 text-gray-400 text-xs font-medium">Read</th>
                  <th className="text-left px-3 py-2 text-gray-400 text-xs font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {list.map(t => (
                  <tr key={t.customer_id} className="border-b border-surface-border/40 last:border-0 hover:bg-white/5/30">
                    <td className="px-3 py-2">
                      <p className="text-white font-medium">{t.customer_name}</p>
                      {t.customer_email && <p className="text-gray-500 text-xs">{t.customer_email}</p>}
                    </td>
                    <td className="px-3 py-2 text-gray-400 text-xs">{fmt(t.sent_at)}</td>
                    <td className="px-3 py-2 text-gray-400 text-xs">{t.read_at ? fmt(t.read_at) : '—'}</td>
                    <td className="px-3 py-2">
                      {t.dismissed ? (
                        <span className="text-xs text-red-400">Dismissed</span>
                      ) : t.read_at ? (
                        <span className="text-xs text-green-400 flex items-center gap-1"><CheckCircle size={11}/> Read</span>
                      ) : (
                        <span className="text-xs text-amber-400 flex items-center gap-1"><Clock size={11}/> Unread</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Promotions() {
  const [showForm, setShowForm] = useState(false)
  const [editPromo, setEditPromo] = useState(null)
  const [pushPromo, setPushPromo] = useState(null)
  const [viewPromo, setViewPromo] = useState(null)
  const [filter, setFilter] = useState('all')
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'promotions'],
    queryFn: promotionService.getAll,
    staleTime: 30 * 1000,
  })

  const createMutation = useMutation({
    mutationFn: promotionService.create,
    onSuccess: () => { qc.invalidateQueries(['admin','promotions']); toast.success('Promotion created!') },
    onError: () => toast.error('Failed to create promotion'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => promotionService.update(id, data),
    onSuccess: () => { qc.invalidateQueries(['admin','promotions']); toast.success('Promotion updated') },
    onError: () => toast.error('Failed to update'),
  })

  const deleteMutation = useMutation({
    mutationFn: promotionService.remove,
    onSuccess: () => { qc.invalidateQueries(['admin','promotions']); toast.success('Promotion deleted') },
    onError: () => toast.error('Failed to delete'),
  })

  const rawPromos = Array.isArray(data) ? data : []

  const filtered = useMemo(() => {
    if (filter === 'all') return rawPromos
    if (filter === 'live') return rawPromos.filter(p => statusOf(p).label === 'Live')
    if (filter === 'scheduled') return rawPromos.filter(p => statusOf(p).label === 'Scheduled')
    if (filter === 'expired') return rawPromos.filter(p => statusOf(p).label === 'Expired' || statusOf(p).label === 'Inactive')
    return rawPromos
  }, [rawPromos, filter])

  const stats = useMemo(() => ({
    live: rawPromos.filter(p => statusOf(p).label === 'Live').length,
    scheduled: rawPromos.filter(p => statusOf(p).label === 'Scheduled').length,
    total_sent: rawPromos.reduce((s, p) => s + (p.sent_count || 0), 0),
    total_read: rawPromos.reduce((s, p) => s + (p.read_count || 0), 0),
  }), [rawPromos])

  const handleSave = async (form) => {
    if (editPromo) {
      await updateMutation.mutateAsync({ id: editPromo.id, data: form })
    } else {
      await createMutation.mutateAsync(form)
    }
  }

  const FILTERS = [
    { key: 'all', label: 'All', count: rawPromos.length },
    { key: 'live', label: '🟢 Live', count: stats.live },
    { key: 'scheduled', label: '🔵 Scheduled', count: stats.scheduled },
    { key: 'expired', label: '🔴 Expired', count: rawPromos.length - stats.live - stats.scheduled },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Gift size={22} className="text-primary"/> Promotions
          </h1>
          <p className="text-gray-400 text-sm mt-0.5">Create and push promotional offers to customers</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => qc.invalidateQueries(['admin','promotions'])} className="btn-secondary flex items-center gap-1.5 text-xs">
            <RefreshCw size={13}/> Refresh
          </button>
          <button onClick={() => { setEditPromo(null); setShowForm(true) }} className="btn-primary flex items-center gap-1.5">
            <Plus size={15}/> New Promotion
          </button>
        </div>
      </div>

      {/* KPI bar */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: 'Live Now', value: stats.live, icon: Zap, color: 'text-green-400', bg: 'bg-green-400/10' },
          { label: 'Scheduled', value: stats.scheduled, icon: Calendar, color: 'text-blue-400', bg: 'bg-blue-400/10' },
          { label: 'Total Sent', value: stats.total_sent, icon: Send, color: 'text-indigo-400', bg: 'bg-indigo-400/10' },
          { label: 'Total Read', value: stats.total_read, icon: Eye, color: 'text-amber-400', bg: 'bg-amber-400/10' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-surface-card border border-surface-border rounded-xl p-3 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center flex-shrink-0`}>
              <Icon size={16} className={color}/>
            </div>
            <div>
              <p className="text-white font-bold text-lg leading-none">{value}</p>
              <p className="text-gray-500 text-xs mt-0.5">{label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              filter === f.key ? 'bg-primary text-white' : 'bg-surface-card border border-surface-border text-gray-400 hover:text-gray-300'
            }`}
          >
            {f.label}
            <span className={`px-1.5 py-0.5 rounded-full text-xs ${filter===f.key?'bg-white/20':'bg-black/40 text-gray-500'}`}>{f.count}</span>
          </button>
        ))}
      </div>

      {/* Promo grid */}
      {isLoading ? (
        <div className="flex justify-center py-16"><Spinner size="lg"/></div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Gift}
          title="No promotions yet"
          message="Create your first promotion and push it to your customers"
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(promo => (
            <PromoCard
              key={promo.id}
              promo={promo}
              onEdit={() => { setEditPromo(promo); setShowForm(true) }}
              onDelete={() => {
                if (confirm(`Delete "${promo.title}"?`)) deleteMutation.mutate(promo.id)
              }}
              onPush={() => setPushPromo(promo)}
              onView={() => setViewPromo(promo)}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <PromotionFormModal
          initial={editPromo}
          onClose={() => { setShowForm(false); setEditPromo(null) }}
          onSave={handleSave}
        />
      )}
      {pushPromo && <PushModal promo={pushPromo} onClose={() => setPushPromo(null)}/>}
      {viewPromo && <TargetsModal promo={viewPromo} onClose={() => setViewPromo(null)}/>}
    </div>
  )
}
